import { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { BaseMessage } from '../BaseMessage';
import { getNumOfSelectableRepos, pluralize } from 'common/utils/utils';
import Text from 'components/base/Text';
import { LINE_HEIGHT, SIZES } from 'common/constants/sizes';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { OAuthProviders } from 'common/utils/assets.utils';
import { Link, Tooltip } from '@material-ui/core';
import { StyledRepoText } from '../Notifications.style';
import { LBButton } from '@linearb/buzz';

const MAX_REPOS_ON_MESSAGE = 8;

const ShowLink = styled(Link)`
  display: block;
  font-size: ${SIZES.large} !important;
  line-height: ${LINE_HEIGHT.large};
`;

const Description = ({ repositories, license = {} }) => {
  const [showMore, setShowMore] = useState(false);
  const moreRepos = repositories.slice(MAX_REPOS_ON_MESSAGE);
  const { reposLeft, maxRepos } = license as any;

  return (
    <>
      {repositories
        .slice(0, showMore ? repositories.length : MAX_REPOS_ON_MESSAGE)
        .map((repository, idx) => {
          const isLastItem = idx === repositories.length - 1;
          return (
            <div key={idx}>
              <Tooltip
                title={`${repository.http_url}`}
                id='increaseTooltipZIndex'
              >
                <StyledRepoText>
                  {repository.name}
                  {!isLastItem && ', '}
                </StyledRepoText>
              </Tooltip>
            </div>
          );
        })}
      {!isEmpty(moreRepos) && (
        <ShowLink onClick={() => setShowMore(prevState => !prevState)}>
          {showMore ? 'show less' : `show ${moreRepos.length} more`}
        </ShowLink>
      )}
      <Text size='large' lineHeight={LINE_HEIGHT.large} color='dodgerBlue'>
        {reposLeft
          ? `You have added ${maxRepos - reposLeft}/${maxRepos} repos`
          : `You have reached max amount of repos (${maxRepos}/${maxRepos})`}
      </Text>
      <p className='text-[1.4rem] mt-4 font-medium'>
        Would you like to add them to your LinearB analysis?
      </p>
    </>
  );
};

const NewReposMessage = ({
  title,
  extra_data = {},
  created_at,
  is_read = false,
  markAsRead,
  deleteMessage,
  addRepositories,
  openRepositoriesModal,
  dedup_key,
  license,
  repositories: _repositories,
  sensorAuthorizationsById
}) => {
  const { repos: repositories, sensor_authorization_id } = extra_data as any;
  if (!repositories || !sensor_authorization_id) {
    return null;
  }
  const newRepositories = repositories.map(
    ({ name, http_url, provider_id }) => ({
      name,
      http_url,
      provider_id,
      sensor_authorization_id
    })
  );

  const repositoriesLeftInLicense = getNumOfSelectableRepos({
    license
  });

  const openModalCb = () =>
    openRepositoriesModal(newRepositories, dedup_key, sensor_authorization_id);

  const addRepositoriesCb = () => addRepositories(newRepositories, dedup_key);

  const sensorName = get(
    sensorAuthorizationsById,
    `[${sensor_authorization_id}].sensor_name`,
    ''
  );

  const logo = get(OAuthProviders, `[${sensorName.toLowerCase()}].logo`, null);

  return (
    <BaseMessage
      icon={logo}
      title={
        title ??
        `${repositories.length} New ${
          repositories.length === 1 ? 'repository' : 'repositories'
        } found!`
      }
      description={
        <Description repositories={repositories} license={license} />
      }
      callToAction={[
        <LBButton
          key='add'
          disabled={!repositoriesLeftInLicense}
          onClick={
            repositoriesLeftInLicense < newRepositories.length
              ? openModalCb
              : addRepositoriesCb
          }
        >
          Yes, add the new{' '}
          {pluralize(repositories.length, 'repo', undefined, false)}
        </LBButton>,
        <LBButton key='edit' onClick={openModalCb}>
          Edit repo list
        </LBButton>
      ]}
      date={moment(created_at)}
      isRead={is_read}
      markAsRead={markAsRead}
      deleteMessage={deleteMessage}
      id={dedup_key}
    />
  );
};

export default NewReposMessage;
